import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import CopyableEmail from '@/components/CopyableEmail';

export default function RiskDisclaimerPage() {
  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[#050814]" data-testid="risk-disclaimer-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-amber-500/[0.04] blur-[140px] pointer-events-none" />
        <div className="relative mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 mb-5">
            <ShieldAlert size={26} className="text-amber-500" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500/80 mb-3">Important Notice</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight">
            Risk <span className="text-amber-500">Disclaimer</span>
          </h1>
          <p className="mt-4 text-xs text-slate-500">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* High-impact warning card */}
      <section className="px-4 sm:px-6 lg:px-8 pb-10">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6 sm:p-7 flex items-start gap-4" data-testid="risk-warning-card">
            <AlertTriangle size={26} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-white mb-2">High Risk Warning</p>
              <p className="text-sm text-white/70 leading-relaxed">
                Trading foreign exchange (Forex) on margin carries a <span className="text-amber-400 font-semibold">high level of risk</span> and may not be suitable for all investors. You could lose some or all of your invested capital. Past performance is not indicative of future results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Sections */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {[
            {
              title: 'General Risk Disclosure',
              body: (
                <>
                  <p>Forex trading involves substantial risk and is not appropriate for every investor. Before deciding to trade foreign exchange you should carefully consider your investment objectives, level of experience and risk appetite. There is a possibility that you could sustain a loss of some or all of your initial investment and therefore you should not invest money that you cannot afford to lose.</p>
                  <p>You should be aware of all the risks associated with foreign exchange trading and seek advice from an independent financial advisor if you have any doubts.</p>
                </>
              ),
            },
            {
              title: 'No Investment Advice',
              body: (
                <>
                  <p>All content, courses, Expert Advisors (EAs), indicators, signals, examples, and any other information provided on this website by 1on1trade and Hettie Nel are for <span className="text-white font-medium">educational and informational purposes only</span>.</p>
                  <p>Nothing on this website constitutes — or should be construed as — financial advice, an investment recommendation, a solicitation, or an offer to buy or sell any financial instrument. 1on1trade is not a registered broker, financial advisor, or asset manager.</p>
                </>
              ),
            },
            {
              title: 'Past Performance & Hypothetical Results',
              body: (
                <>
                  <p>Hypothetical or simulated performance results have inherent limitations. Unlike actual trading records, simulated results do not represent real trading and may under- or over-compensate for the impact of certain market factors such as liquidity, slippage and execution.</p>
                  <p>No representation is being made that any account will, or is likely to, achieve profits or losses similar to those discussed on this website, in our courses, or in any of our materials.</p>
                </>
              ),
            },
            {
              title: 'Expert Advisors (EAs) & Indicators',
              body: (
                <>
                  <p>The Expert Advisors, indicators, and automated trading tools sold or distributed via 1on1trade are technical software products. They are sold "as is" with no guarantee of profitability, performance, or fitness for any particular purpose.</p>
                  <p>You are solely responsible for testing, configuring, and monitoring any automated tool you deploy on your trading account. We strongly recommend extensive testing on a demo account before risking real capital.</p>
                </>
              ),
            },
            {
              title: 'Your Responsibility',
              body: (
                <>
                  <p>By using this website, purchasing any product, or enrolling in any course, you acknowledge and agree that:</p>
                  <ul className="list-disc pl-5 mt-3 space-y-2">
                    <li>You are solely responsible for your own trading decisions.</li>
                    <li>You will not hold 1on1trade, Hettie Nel, or any affiliated party liable for any losses, costs, or damages incurred as a result of trading.</li>
                    <li>You will only trade with capital you can afford to lose.</li>
                    <li>You have read and understood this Risk Disclaimer in full.</li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Regulatory Notice',
              body: (
                <p>Different jurisdictions have different rules regarding the marketing, sale and use of leveraged forex products. It is your responsibility to ensure that your participation complies with the laws and regulations of your country of residence.</p>
              ),
            },
          ].map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7" data-testid={`risk-section-${i}`}>
              <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">{s.title}</h2>
              <div className="mt-3 text-sm text-white/60 leading-relaxed space-y-3">
                {s.body}
              </div>
            </div>
          ))}

          {/* Contact note */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-7 flex items-start gap-4" data-testid="risk-contact-note">
            <Info size={20} className="text-sky-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-white mb-1">Questions about this disclaimer?</p>
              <p className="text-sm text-white/55 leading-relaxed">
                Reach out at <CopyableEmail email="info@1on1trade.com" className="text-sky-400 hover:text-sky-300 transition-colors" iconSize={12} testId="risk-email" /> or via our <a href="/contact" className="text-sky-400 hover:text-sky-300 transition-colors">contact page</a>. By continuing to use the site, you confirm that you have read, understood and accepted this Risk Disclaimer.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
